import { Button, Textarea } from "@aix/ui"
import { useLocalStorage } from "@aix/ui"
import { History, Loader2, RotateCcw, Save, Sparkles, Code, Copy, Check } from "lucide-react"
import { useEffect, useState } from "react"
import toast from "react-hot-toast"
import { trpc } from "~/lib/trpc"

interface TemplateVersion {
  timestamp: number
  data: any
  brainDump?: string
}

interface CoachTemplateEditorProps {
  templateId?: string // undefined for "new", UUID for edit
  onSave?: (template: any) => void
}

export default function CoachTemplateEditor({ templateId, onSave }: CoachTemplateEditorProps) {
  const isEditMode = !!templateId

  // Load existing template if editing
  const { data: existingTemplate, isLoading: loadingTemplate } = trpc.coachTemplates.get.useQuery(
    { templateId: templateId || "" }, // Provide fallback empty string
    { enabled: isEditMode && !!templateId }, // Only enable if we have a valid templateId
  )

  // State
  const [step, setStep] = useState<"brain-dump" | "review">("brain-dump")
  const [inputMode, setInputMode] = useState<"ai" | "manual">("ai") // Default to AI generation
  const [brainDump, setBrainDump] = useState("")
  const [manualJson, setManualJson] = useState("")
  const [generatedTemplate, setGeneratedTemplate] = useState<any>(null)
  const [isGenerating, setIsGenerating] = useState(false)
  const [editInstructions, setEditInstructions] = useState("")
  const [copiedPrompt, setCopiedPrompt] = useState(false)

  // ChatGPT prompt to help users create good coaches
  const chatGptPrompt = `I want to create a comprehensive coach template for my CoachOS system. Here's what I need help with:

**My Coach Concept:**
[Describe your coaching domain - e.g., "Half marathon training for beginners", "Launching a SaaS product", "Learning guitar", etc.]

**Target Audience:**
[Who is this for? What level are they starting at?]

**Desired Outcome:**
[What should they achieve by the end?]

**Duration:**
[How many weeks should this program last?]

**Coach Tone:**
[Should the coach be: chill/supportive, tough-love/strict, professional/formal, playful/fun?]

---

Please help me create a detailed coach template that includes:

1. **Core Goals** (2-4 goals with priority, target metrics, and why they matter)

2. **Trackable Metrics** (5-7 key metrics to track progress):
   - For each trackable: prompt text, data type (number, boolean, scale, text), unit (if applicable), and help text

3. **Weekly Structure Example** (What does a typical week look like? Break down by day)

4. **Common Patterns** (3-6 patterns users typically follow, with confidence level and source)

5. **Expert Tips** (10-15 actionable tips categorized by: planning, execution, mindset, recovery, nutrition, etc. - specify when to share each tip)

6. **Success Metrics** (5-6 key metrics that define success, with target and rationale)

7. **Recommended Resources** (3-5 articles, books, podcasts, or tools that would help)

8. **Intake Questions** (Questions to ask during onboarding, organized by category):
   - Goals (required + optional questions)
   - Baseline (current state assessment)
   - Availability (time constraints)
   - Constraints (blockers, past failures, injuries, etc.)
   - Motivation (why this matters to them)

9. **Planning Parameters**:
   - Recommended phase structure (e.g., "3 phases: foundation, growth, mastery")
   - Key ratios and rules (e.g., "increase volume by max 10% per week")
   - Recovery guidelines
   - Adjustment triggers

10. **Phase Breakdown** (2-4 phases with clear progression):
    - For each phase: name, focus, duration, milestones, common challenges, coach focus

11. **Sample Week Structures** (2-3 examples from different phases showing daily activities)

12. **Common Questions & Answers** (6-10 FAQs users typically have)

Format the output as a structured brain dump that I can paste into my coach template editor. Be specific, actionable, and detailed!`

  // Example brain dump text
  const exampleBrainDump = `This is a half marathon training coach. It's for beginners who want to complete their first 13.1 miles. The program should be 12 weeks long, with a chill/supportive tone.

Key trackables:
- Daily mileage
- Did they complete the run
- How they felt (1-10 scale)
- Sleep hours

The program should have 3 phases:
1. Base building (weeks 1-4) - focus on consistency
2. Endurance building (weeks 5-9) - increase distance gradually
3. Peak and taper (weeks 10-12) - hit peak distance, then rest before race

Common tips:
- Most runs should be conversational pace
- Long runs on weekends
- Walk breaks are totally fine
- Listen to your body

Success means finishing comfortably and enjoying the process, not chasing a time.`

  // Version history (last 3 versions in localStorage)
  const [versionHistory, setVersionHistory] = useLocalStorage<TemplateVersion[]>(
    `coach-template-versions-${templateId || "new"}`,
    [],
  )

  // Mutations
  const createMutation = trpc.coachTemplates.create.useMutation()
  const generateMutation = trpc.coachTemplates.generateFromBrainDump.useMutation()
  const validateMutation = trpc.coachTemplates.validateManualInput.useMutation()

  // Load existing template into editor
  useEffect(() => {
    if (existingTemplate && !generatedTemplate) {
      setGeneratedTemplate(existingTemplate)
      setStep("review")
    }
  }, [existingTemplate])

  // Copy ChatGPT prompt to clipboard
  const handleCopyPrompt = async () => {
    try {
      await navigator.clipboard.writeText(chatGptPrompt)
      setCopiedPrompt(true)
      toast.success("Prompt copied to clipboard!")
      setTimeout(() => setCopiedPrompt(false), 2000)
    } catch (error) {
      toast.error("Failed to copy prompt")
    }
  }

  // Generate template from brain dump (AI mode)
  const handleGenerate = async () => {
    if (!brainDump.trim()) {
      toast.error("Please provide some information about the template")
      return
    }

    setIsGenerating(true)
    try {
      const generated = await generateMutation.mutateAsync({
        brainDump: brainDump.trim(),
      })

      setGeneratedTemplate(generated)

      // Save to version history
      const newVersion: TemplateVersion = {
        timestamp: Date.now(),
        data: generated,
        brainDump: brainDump,
      }
      setVersionHistory([newVersion, ...versionHistory].slice(0, 3))

      setStep("review")
      toast.success("Template generated!")
    } catch (error: any) {
      toast.error("Failed to generate template: " + error.message)
    } finally {
      setIsGenerating(false)
    }
  }

  // Validate and parse manual JSON input
  const handleValidateManualInput = async () => {
    if (!manualJson.trim()) {
      toast.error("Please provide JSON input")
      return
    }

    setIsGenerating(true)
    try {
      const result = await validateMutation.mutateAsync({
        rawJson: manualJson.trim(),
      })

      setGeneratedTemplate(result.template)

      // Save to version history
      const newVersion: TemplateVersion = {
        timestamp: Date.now(),
        data: result.template,
        brainDump: "Manual JSON input",
      }
      setVersionHistory([newVersion, ...versionHistory].slice(0, 3))

      setStep("review")
      toast.success("Template validated successfully!")
    } catch (error: any) {
      console.error("Validation error:", error)

      // Extract error message - handle tRPC error structure
      let errorMessage = "Failed to validate template"

      if (error?.message) {
        errorMessage = error.message
      } else if (error?.data?.message) {
        errorMessage = error.data.message
      } else if (typeof error === "string") {
        errorMessage = error
      }

      // Show detailed error message in toast
      toast.error(errorMessage, {
        duration: 10000, // Show for 10 seconds for longer error messages
        style: {
          maxWidth: "700px",
          whiteSpace: "pre-wrap", // Preserve line breaks
          fontSize: "13px",
        },
      })
    } finally {
      setIsGenerating(false)
    }
  }

  // Regenerate with edit instructions
  const handleRegenerate = async () => {
    if (!editInstructions.trim()) {
      toast.error("Please provide edit instructions")
      return
    }

    setIsGenerating(true)
    try {
      const updated = await generateMutation.mutateAsync({
        brainDump: editInstructions.trim(),
        existingTemplate: generatedTemplate,
        editInstructions: editInstructions.trim(),
      })

      setGeneratedTemplate(updated)

      // Save to version history
      const newVersion: TemplateVersion = {
        timestamp: Date.now(),
        data: updated,
        brainDump: editInstructions,
      }
      setVersionHistory([newVersion, ...versionHistory].slice(0, 3))

      setEditInstructions("")
      toast.success("Template regenerated!")
    } catch (error: any) {
      toast.error("Failed to regenerate: " + error.message)
    } finally {
      setIsGenerating(false)
    }
  }

  // Revert to previous version
  const handleRevert = (version: TemplateVersion) => {
    setGeneratedTemplate(version.data)
    toast.success("Reverted to previous version")
  }

  // Save template
  const handleSave = async () => {
    if (!generatedTemplate) return

    try {
      const saved = await createMutation.mutateAsync(generatedTemplate)
      toast.success("Template saved!")
      onSave?.(saved)
    } catch (error: any) {
      console.error("Save error:", error)

      // Extract error message - handle tRPC error structure
      let errorMessage = "Failed to save template"

      if (error?.message) {
        errorMessage = error.message
      } else if (error?.data?.message) {
        errorMessage = error.data.message
      } else if (typeof error === "string") {
        errorMessage = error
      }

      // Show detailed error message in toast
      toast.error(errorMessage, {
        duration: 10000, // Show for 10 seconds for longer error messages
        style: {
          maxWidth: "700px",
          whiteSpace: "pre-wrap", // Preserve line breaks
          fontSize: "13px",
        },
      })
    }
  }

  // Only show loading spinner if we're actually loading an existing template
  if (isEditMode && loadingTemplate) {
    return (
      <div className="flex items-center justify-center h-full">
        <Loader2 className="w-8 h-8 animate-spin text-primary" />
      </div>
    )
  }

  return (
    <div className="h-full flex flex-col bg-background">
      {/* Brain Dump Step */}
      {step === "brain-dump" && (
        <div className="flex-1 flex flex-col p-6">
          <div className="max-w-3xl mx-auto w-full flex-1 flex flex-col">
            {/* ChatGPT Helper Prompt */}
            <div className="mb-6 border-2 border-primary/20 rounded-lg p-4 bg-primary/5">
              <div className="flex items-start justify-between gap-4">
                <div className="flex-1">
                  <h3 className="text-sm font-bold text-primary mb-1 flex items-center gap-2">
                    <Sparkles className="w-4 h-4" />
                    Need help brainstorming your coach?
                  </h3>
                  <p className="text-xs text-muted-foreground">
                    Copy this ChatGPT prompt to help you create a comprehensive coach template with all the right
                    details
                  </p>
                </div>
                <Button onClick={handleCopyPrompt} variant="outline" size="sm" className="shrink-0">
                  {copiedPrompt ? (
                    <>
                      <Check className="w-4 h-4 mr-2 text-primary" />
                      Copied!
                    </>
                  ) : (
                    <>
                      <Copy className="w-4 h-4 mr-2" />
                      Copy Prompt
                    </>
                  )}
                </Button>
              </div>
            </div>

            <div className="mb-6">
              <h2 className="text-2xl font-bold text-primary mb-2">
                {isEditMode ? "Edit Coach Template" : "Create New Coach Template"}
              </h2>
              <p className="text-muted-foreground mb-4">
                {inputMode === "ai"
                  ? "Brain dump all the information about this coach template. The AI will generate a structured template for you."
                  : "Paste your template JSON below. It will be validated against the schema."}
              </p>

              {/* Mode Toggle */}
              <div className="flex gap-2 mb-4">
                <Button
                  onClick={() => setInputMode("ai")}
                  variant={inputMode === "ai" ? "default" : "outline"}
                  size="sm"
                  className={inputMode === "ai" ? "bg-primary text-primary-foreground" : ""}
                >
                  <Sparkles className="w-4 h-4 mr-2" />
                  AI Generation
                </Button>
                <Button
                  onClick={() => setInputMode("manual")}
                  variant={inputMode === "manual" ? "default" : "outline"}
                  size="sm"
                  className={inputMode === "manual" ? "bg-primary text-primary-foreground" : ""}
                >
                  <Code className="w-4 h-4 mr-2" />
                  Manual JSON
                </Button>
              </div>
            </div>

            <div className="flex-1 flex flex-col">
              {inputMode === "ai" ? (
                <>
                  <label className="text-sm font-medium mb-2">Brain Dump</label>
                  <Textarea
                    value={brainDump}
                    onChange={(e) => setBrainDump(e.target.value)}
                    placeholder={exampleBrainDump}
                    className="flex-1 font-mono text-sm"
                  />
                </>
              ) : (
                <>
                  <label className="text-sm font-medium mb-2">Template JSON</label>
                  <Textarea
                    value={manualJson}
                    onChange={(e) => setManualJson(e.target.value)}
                    placeholder='Paste your template JSON here. Example structure:

{
  "name": "Half Marathon Coach",
  "icon": "🏃",
  "description": "12-week beginner-friendly half marathon training program",
  "category": "health",
  "type": "side-quest",
  "defaultTone": "chill",
  "defaultDurationWeeks": 12,
  "templateContext": {
    "default_goals": [...],
    "suggested_trackables": [...],
    "intake_configuration": {...},
    ...
  },
  "version": "1.0.0",
  "changelog": "Initial release"
}'
                    className="flex-1 font-mono text-xs"
                  />
                </>
              )}
            </div>

            <div className="flex gap-3 mt-6">
              {inputMode === "ai" ? (
                <>
                  <Button
                    onClick={handleGenerate}
                    disabled={isGenerating || !brainDump.trim()}
                    className="bg-primary text-primary-foreground"
                  >
                    {isGenerating ? (
                      <>
                        <Loader2 className="w-4 h-4 mr-2 animate-spin" />
                        Generating...
                      </>
                    ) : (
                      <>
                        <Sparkles className="w-4 h-4 mr-2" />
                        Generate Template
                      </>
                    )}
                  </Button>
                  <Button onClick={() => setBrainDump(exampleBrainDump)} variant="outline" disabled={isGenerating}>
                    Use Example
                  </Button>
                </>
              ) : (
                <Button
                  onClick={handleValidateManualInput}
                  disabled={isGenerating || !manualJson.trim()}
                  className="bg-primary text-primary-foreground"
                >
                  {isGenerating ? (
                    <>
                      <Loader2 className="w-4 h-4 mr-2 animate-spin" />
                      Validating...
                    </>
                  ) : (
                    <>
                      <Code className="w-4 h-4 mr-2" />
                      Validate & Continue
                    </>
                  )}
                </Button>
              )}
            </div>
          </div>
        </div>
      )}

      {/* Review Step */}
      {step === "review" && generatedTemplate && (
        <div className="flex-1 flex gap-4 p-6 overflow-hidden">
          {/* Left: JSON Preview */}
          <div className="flex-1 flex flex-col border-2 border-border rounded-lg bg-secondary overflow-hidden">
            <div className="border-b-2 border-border p-4 bg-background">
              <h3 className="font-bold text-primary">Generated Template (JSON)</h3>
              <p className="text-xs text-muted-foreground mt-1">Preview the generated template structure</p>
            </div>
            <div className="flex-1 overflow-auto p-4">
              <pre className="text-xs font-mono whitespace-pre-wrap">{JSON.stringify(generatedTemplate, null, 2)}</pre>
            </div>
          </div>

          {/* Right: Edit Instructions & Actions */}
          <div className="w-96 flex flex-col gap-4">
            {/* Edit Instructions */}
            <div className="border-2 border-border rounded-lg bg-secondary p-4">
              <h3 className="font-bold text-primary mb-3">Edit Instructions</h3>
              <Textarea
                value={editInstructions}
                onChange={(e) => setEditInstructions(e.target.value)}
                placeholder="What would you like to change?

Examples:
- Add a trackable for cross-training
- Change tone to 'tough-love'
- Make it 16 weeks instead of 12
- Add more recovery tips"
                className="h-32 text-sm mb-3"
              />
              <Button onClick={handleRegenerate} disabled={isGenerating || !editInstructions.trim()} className="w-full">
                {isGenerating ? (
                  <>
                    <Loader2 className="w-4 h-4 mr-2 animate-spin" />
                    Regenerating...
                  </>
                ) : (
                  <>
                    <RotateCcw className="w-4 h-4 mr-2" />
                    Regenerate
                  </>
                )}
              </Button>
            </div>

            {/* Version History */}
            {versionHistory.length > 0 && (
              <div className="border-2 border-border rounded-lg bg-secondary p-4">
                <h3 className="font-bold text-primary mb-3 flex items-center gap-2">
                  <History className="w-4 h-4" />
                  Version History
                </h3>
                <p className="text-xs text-muted-foreground mb-3">Last 3 iterations (local)</p>
                <div className="space-y-2">
                  {versionHistory.map((version, index) => (
                    <div key={version.timestamp} className="bg-background border border-border rounded-md p-3">
                      <div className="flex items-center justify-between mb-1">
                        <span className="text-xs font-medium">Version {versionHistory.length - index}</span>
                        <span className="text-xs text-muted-foreground">
                          {new Date(version.timestamp).toLocaleTimeString()}
                        </span>
                      </div>
                      {version.brainDump && (
                        <p className="text-xs text-muted-foreground line-clamp-2 mb-2">{version.brainDump}</p>
                      )}
                      <Button
                        onClick={() => handleRevert(version)}
                        variant="outline"
                        size="sm"
                        className="w-full text-xs"
                      >
                        Revert to this version
                      </Button>
                    </div>
                  ))}
                </div>
              </div>
            )}

            {/* Actions */}
            <div className="border-2 border-border rounded-lg bg-secondary p-4">
              <h3 className="font-bold text-primary mb-3">Actions</h3>
              <div className="space-y-2">
                <Button
                  onClick={handleSave}
                  disabled={createMutation.isLoading}
                  className="w-full bg-primary text-primary-foreground"
                >
                  {createMutation.isLoading ? (
                    <>
                      <Loader2 className="w-4 h-4 mr-2 animate-spin" />
                      Saving...
                    </>
                  ) : (
                    <>
                      <Save className="w-4 h-4 mr-2" />
                      Save Template
                    </>
                  )}
                </Button>
                <Button
                  onClick={() => {
                    setBrainDump("")
                    setStep("brain-dump")
                  }}
                  variant="outline"
                  className="w-full"
                >
                  Start Over
                </Button>
              </div>
            </div>
          </div>
        </div>
      )}
    </div>
  )
}
